import BackendAuthHandler from 'BackendAuthHandler';
import {
  AcceptAdvisorInvitationRequest,
  AcceptAdvisorInvitationResponse,
  AdvisorInvitationVerificationResponse,
  RejectAdvisorInvitationRequest,
  RejectAdvisorInvitationResponse,
} from '../../models/Advisor';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import Investor from '../../models/Investor';
import ShareData from '../ShareData';
import type { CompletedShareData } from './types';
import { BaseBackendServiceFundDomain } from './BaseBackendServiceFundDomain';

export class BaseBackendServiceInvestorClient extends BaseBackendServiceFundDomain {
  getCompleteInvestments(): Promise<Investor[]> {
    return this.authApiGetAuthed(
      'contacts/investments?includeComplete=true',
      'Network error while fetching completed investments',
      (json) => json.data as Investor[],
    );
  }

  /**
   * Targeted lookup by fund opportunity ID. Unlike the plain investments list,
   * the backend does not exclude COMPLETE-status funds when fundOpportunityId
   * is supplied (investorQueryHandlers.ts only applies that exclusion to the
   * unfiltered list) — this is how the native app resolves closed funds.
   */
  getInvestorsByFundOpportunityId(fundOpportunityId: string): Promise<Investor[]> {
    return this.authApiGetAuthed(
      `contacts/investments?fundOpportunityId=${encodeURIComponent(fundOpportunityId)}`,
      'Network error while fetching investment for fund opportunity',
      (json) => json.data as Investor[],
    );
  }

  async requestImageAccess(imagePrefixes: string[]): Promise<void> {
    return this.authFetchPromise(async (token) => {
      if (!token) {
        return;
      }

      const response = await this.authenticatedFetch(this.apiURL('requestImageAccess'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ images: imagePrefixes }),
      });

      if (!response.ok) {
        throw new Error(`Failed to request image access: ${response.status}`);
      }
    });
  }

  setRolesCache(): Promise<void> {
    return this.authApiMutateAuthed<void>(
      '/api/setRolesCache',
      'POST',
      undefined,
      () => undefined,
      true,
    );
  }

  checkFundOpportunityPDFAvailable(id: string): Promise<boolean> {
    return this.authApiGetAuthed(
      `/api/fundOpportunities/${id}/pdfAvailable`,
      'Failed to check PDF availability',
      (data) => data.available as boolean,
      true,
    );
  }

  async trackEmailClick(
    actionLogId: string,
    clickedUrl: string,
    options?: {
      linkId?: string | null;
      linkURL?: string | null;
    },
  ): Promise<{ redirectUrl?: string; requiresBrowserRedirect?: boolean }> {
    console.log('[trackEmailClick] Starting', { actionLogId, clickedUrl, options });
    try {
      let authToken: string | null = null;
      try {
        const handler = new BackendAuthHandler();
        const tokenResult = await handler.withAuthToken();
        authToken = tokenResult;
        console.log('[trackEmailClick] Auth token retrieved:', authToken ? 'yes' : 'no');
      } catch (error: any) {
        if (error?.message?.includes('setAuthContextOnBackendService')) {
          console.log('[trackEmailClick] Auth context not set, proceeding without token');
        } else {
          console.log(
            '[trackEmailClick] Auth token retrieval skipped (expected if not logged in):',
            error?.message || error,
          );
        }
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const apiUrl = this.apiURL('emails/track-click');
      console.log('[trackEmailClick] Making request to:', apiUrl, { headers });

      const response = await this.authenticatedFetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          actionLogId,
          clickedUrl,
          linkId: options?.linkId || null,
          linkURL: options?.linkURL || null,
        }),
        redirect: 'manual',
        credentials: 'include',
        mode: 'same-origin',
      });

      console.log('[trackEmailClick] Fetch response received', {
        status: response.status,
        statusText: response.statusText,
        type: response.type,
        ok: response.ok,
      });

      const data = await response.json();

      return { redirectUrl: data.redirectUrl, requiresBrowserRedirect: !!data.externalLink };
    } catch (error) {
      console.error('[trackEmailClick] Unexpected error:', error);
      return Promise.resolve({});
    }
  }

  createUserWithEmailAndPassword(invitationToken: string, password: string) {
    return this.authenticatedFetch(this.apiURL(`contacts/invitation/accept/${invitationToken}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    }).then(async (response) => {
      if (response.status === 200 || response.status === 304) return;
      if (response.status === 404) throw 'Invitation not found';
      const data = await response.json();
      throw data.error?.message;
    });
  }

  validateInvitationToken(invitationToken: string): Promise<{ valid: boolean; firstName: string }> {
    return this.authenticatedFetch(
      this.apiURL(`contacts/invitation/validate/${invitationToken}`),
    ).then(async (response) => {
      const data = (await response.json()) as { valid: boolean; firstName: string };
      if (response.status === 410) throw 'EXPIRED';
      if (![200, 304].includes(response.status)) throw (data as any).error?.message;
      return data;
    });
  }

  suggestEOI(
    investorId: string,
    suggestedIndicativeInvestment: number,
    comment?: string,
  ): Promise<{ id: string; error: string | null }> {
    const body: { suggestedIndicativeInvestment: number; comment?: string } = {
      suggestedIndicativeInvestment,
    };
    if (comment?.trim()) {
      body.comment = comment.trim();
    }
    return this.authPostJson(`contacts/investments/${investorId}/suggestEOI`, body);
  }

  acceptSuggestedEOI(investorId: string): Promise<{ id: string; error: string | null }> {
    return this.authPostJson(`contacts/investments/${investorId}/acceptSuggestedEOI`);
  }

  rejectSuggestedEOI(investorId: string): Promise<{ id: string; error: string | null }> {
    return this.authPostJson(`contacts/investments/${investorId}/rejectSuggestedEOI`);
  }

  withdrawSuggestedEOI(investorId: string): Promise<{ id: string; error: string | null }> {
    return this.authPostJson(`contacts/investments/${investorId}/withdrawSuggestedEOI`);
  }

  submitEoi(investorId: string, indicativeInvestment: number): Promise<boolean> {
    return this.authApiMutateAuthed(
      `contacts/investments/${investorId}/submitEoi`,
      'POST',
      { investorId, indicativeInvestment },
      (json) => json as boolean,
    );
  }

  withdrawEoi(investorId: string): Promise<boolean> {
    return this.authApiMutateAuthed(
      `contacts/investments/${investorId}/withdrawEOI`,
      'POST',
      { investorId },
      (json) => json as boolean,
    );
  }

  shareItem(modelId: string, modelType: string, firstName: string, email: string): Promise<void> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL('shareItem'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ modelId, modelType, firstName, email }),
      });
      if (await this.handleAuthRedirect(response)) return;
      if (response.status === 200) return;
      throw 'Unable to share item';
    });
  }

  getAiDisclaimerStatus(): Promise<{
    aiChatDisclaimerAccepted: boolean;
    allowedForImpersonation?: boolean;
  }> {
    return this.authApiGet('contacts/ai-chat-disclaimer-status', (payload) => {
      const data = payload?.data || payload;
      return {
        aiChatDisclaimerAccepted: Boolean(data?.aiChatDisclaimerAccepted),
        allowedForImpersonation: data?.allowedForImpersonation,
      };
    });
  }

  acceptAiDisclaimer(): Promise<void> {
    return this.authApiPost('contacts/accept-ai-chat-disclaimer').then(() => undefined);
  }

  verifyAdvisorInvitation(invitationId: string): Promise<AdvisorInvitationVerificationResponse> {
    return this.authApiGetData(
      `advisors/verify/${invitationId}`,
      'Network error while verifying advisor invitation',
    );
  }

  acceptAdvisorInvitation(
    invitationId: string,
    acceptanceData: AcceptAdvisorInvitationRequest,
  ): Promise<AcceptAdvisorInvitationResponse> {
    return this.authApiPostData(
      `advisors/accept/${invitationId}`,
      acceptanceData,
      'Network error while accepting advisor invitation',
    );
  }

  rejectAdvisorInvitation(
    invitationId: string,
    rejectionData?: RejectAdvisorInvitationRequest,
  ): Promise<RejectAdvisorInvitationResponse> {
    return this.authApiPostData(
      `advisors/reject/${invitationId}`,
      rejectionData || {},
      'Network error while rejecting advisor invitation',
    );
  }

  retrieveShareData(shareId: string): Promise<ShareData> {
    return this.authPostJson('retrieveShareData', { shareId });
  }

  completeShareRegistration(data: CompletedShareData): Promise<void> {
    return this.authApiMutateAuthed<void>(
      'completeShareRegistration',
      'POST',
      data,
      () => undefined,
    );
  }

  getInvestmentLinkableInvestmentEntities(): Promise<InvestmentEntity[]> {
    return this.authApiGetAuthed(
      'investment-entities/linkableForInvestor',
      'Unable to retrieve investment entities',
      (json) => json.data || [],
    );
  }
}
